// Notifications (contracts/admin-api.md "Notifications") and, since 0015, announcements (contracts/messaging.md
// §1): a push and/or an inbox message with localized content, an image, a detail screen and a button. Kept
// apart from client.ts / queries.ts like Ads and the Studio.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";
import { ACTIONS, APP_LINK_LABEL, APP_LINKS, type AppLink, CONTENT_LIMITS, Content, ImageRef, type MessageAction } from "./messaging";

export const BROADCAST_KINDS = ["service", "news"] as const;
export const BROADCAST_AUDIENCES = ["all", "not_played_today", "player"] as const;
/** The push tap target (and the button target for action `link`): every in-app place since 0015. */
export const BROADCAST_LINKS = APP_LINKS;
export type BroadcastKind = (typeof BROADCAST_KINDS)[number];
export type BroadcastAudience = (typeof BROADCAST_AUDIENCES)[number];
export type BroadcastLink = AppLink;

export const TITLE_MAX = CONTENT_LIMITS.title;
export const BODY_MAX = CONTENT_LIMITS.body;

const count = z.number().int().min(0);

export const Broadcast = z.object({
  id: z.string(),
  kind: z.enum(BROADCAST_KINDS),
  audience: z.enum(BROADCAST_AUDIENCES),
  target: z.object({ userId: z.string(), handle: z.string().nullable() }).nullable(),
  /** English. */
  title: z.string(),
  /** English. */
  body: z.string(),
  content: Content,
  image: ImageRef.nullable(),
  action: z.enum(ACTIONS),
  link: z.enum(APP_LINKS),
  url: z.string().nullable(),
  announcementId: z.string().nullable(),
  push: z.boolean(),
  inbox: z.boolean(),
  status: z.enum(["sending", "done"]),
  recipients: count,
  counts: z.object({ waiting: count, sent: count, failed: count, dryRun: count, skipped: count, expired: count }),
  /** Players who opened the detail in the inbox. */
  reads: count,
  /** Players who tapped the button. */
  taps: count,
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type Broadcast = z.infer<typeof Broadcast>;

export const Broadcasts = z.object({ items: z.array(Broadcast) });
/** `recipients`: the push right now; `inbox`: players who would see it in the inbox. */
export const BroadcastPreview = z.object({ recipients: count, inbox: count });

/** POST /broadcasts. `title` / `body` are the 0014 shorthand for `content.en`: send one or the other. */
export interface BroadcastDraft {
  kind: BroadcastKind;
  audience: BroadcastAudience;
  userId?: string;
  title?: string;
  body?: string;
  content?: Content;
  link?: BroadcastLink;
  imageId?: string;
  action?: MessageAction;
  url?: string;
  announcementId?: string;
  push?: boolean;
  inbox?: boolean;
}

export const broadcastsApi = {
  list: (signal?: AbortSignal) => request(Broadcasts, "GET", "/broadcasts", { signal }),
  preview: (kind: BroadcastKind, audience: BroadcastAudience, userId: string | undefined, signal?: AbortSignal) =>
    request(BroadcastPreview, "GET", "/broadcasts/preview", { query: { kind, audience, ...(userId ? { userId } : {}) }, signal }),
  send: (draft: BroadcastDraft) => request(Broadcast, "POST", "/broadcasts", { body: draft }),
};

export const broadcastKeys = {
  all: ["broadcasts"] as const,
  list: () => ["broadcasts", "list"] as const,
  preview: (kind: BroadcastKind, audience: BroadcastAudience, userId: string | undefined) => ["broadcasts", "preview", kind, audience, userId ?? null] as const,
};

/** The history refreshes every 5 s while anything is still being delivered (quiet hours). */
export const useBroadcasts = () =>
  useQuery({
    queryKey: broadcastKeys.list(),
    queryFn: ({ signal }) => broadcastsApi.list(signal),
    refetchInterval: (q) => (q.state.data?.items.some((b) => b.status === "sending") ? 5_000 : false),
  });

export const useBroadcastPreview = (kind: BroadcastKind, audience: BroadcastAudience, userId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: broadcastKeys.preview(kind, audience, userId),
    queryFn: ({ signal }) => broadcastsApi.preview(kind, audience, userId, signal),
    enabled: enabled && (audience !== "player" || Boolean(userId)),
    placeholderData: keepPreviousData,
  });

export function useSendBroadcast() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: BroadcastDraft) => broadcastsApi.send(draft),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: broadcastKeys.all });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const KIND_LABEL: Record<BroadcastKind, string> = { service: "Service message", news: "News & events" };
export const AUDIENCE_LABEL: Record<BroadcastAudience, string> = { all: "Everyone", not_played_today: "Haven't played today", player: "One player" };
export const LINK_LABEL: Record<BroadcastLink, string> = APP_LINK_LABEL;
